/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { LanguageService } from './language.service';
import { LogService } from './log.service';
import { NotificationModel } from '../../models';
import { RbacService } from './rbac.service';
import { Subject } from 'rxjs';
import { UtilsService } from './utils.service';
import { environment } from '../../../../environments/environment';

/**
 * Available Options:
 * hideTypeMessage: boolean - Determines whether we show the type of error in the message or not. E.G. "Warning: MESSAGE_HERE"
 * system: boolean - Determines whether the notification is a 'system-wide' message, hence showing at the top
 * url: Specify a location that the notifications will navigate too
 * static: Static notifications that cannot be dismissed
 * header: Top of header bar
 */

@Injectable({ providedIn: 'root' })
export class NotifyService {
    /**
     * Javascript version of an ENUM, yuck. Level for types of notifications.
     */
    public level = {
        ERROR: 1,
        WARNING: 2,
        SUCCESS: 3,
        INFO: 4,
        DEBUG: 5
    };

    /**
     * An emitter to let components know the notification array has been updated
     */
    public updateNotificationsEmitter: Subject<NotificationModel[]> = new Subject<NotificationModel[]>();

    /**
     * An emitter to trigger the toaster slide in.
     */
    public addToToasterEmitter: Subject<NotificationModel> = new Subject<NotificationModel>();

    /**
     * An emitter to trigger the toaster slide-out.
     */
    public removeFromToasterEmitter: Subject<NotificationModel> = new Subject<NotificationModel>();

    /**
     * An emitter to trigger the toaster slide-out.
     */
    public clearToasterEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * An array of notifications for the header
     */
    private _notifications: NotificationModel[] = [];

    /**
     * Local notifications mute
     */
    private _muteLocal: boolean = false;

    public constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private rbacService: RbacService,
        private utilsService: UtilsService,
    ) {
    }

    get notifications() {
        return this._notifications;
    }

    set notifications(notifications: NotificationModel[]) {
        this._notifications = notifications;
    }

    /**
     * Get all notifications.
     */
    public getCopyOfNotifications(): NotificationModel[] {
        return this.utilsService.duplicateObject(this.notifications);
    }

    /**
     * Stop notifications from being set
     */
    public muteLocalNotifications(): void {
        this._muteLocal = true;
    }

    /**
     * Allow notifications to show after muting.
     */
    public unmuteLocalNotifications(): void {
        this._muteLocal = false;
    }

    /**
     * Get notification by ID.
     * @param id
     */
    public getNotificationsById(id: string): NotificationModel {
        return this.notifications.find(item => item.id === id);
    }

    /**
     * Remove a static notifications on any page route
     * @param id
     */
    public removeNotificationById(id: string): void {
        this.logService.debug('NotifyService:removeStatic()', 'Attempting to remove notifications ' + id);
        const deleteIndex = this.notifications.findIndex(item => item.id === id);

        if (deleteIndex !== -1) {
            this.removeFromToasterEmitter.next(this.notifications[deleteIndex]);
            this.notifications.splice(deleteIndex, 1);
            this.updateNotificationsEmitter.next(this.notifications);
        } else {
            this.logService.debug('NotifyService:removeStatic()', 'Attempting to remove notifications ' + id + ' failed');
        }
    }

    /**
     * Hide a notification, so it only shows on the notifications page not on the structure
     * @param id
     */
    public hideNotificationById(id: string): void {
        this.logService.debug('NotifyService:hideNotificationById()', 'Attempting to hide notifications ' + id);
        const index = this.notifications.findIndex(item => item.id === id);
        if (index !== -1) {
            const clone = this.utilsService.duplicateObject(this.notifications[index]);
            this.removeFromToasterEmitter.next(clone);
            const timerId = setTimeout(() => {
                this.notifications[index].hidden = true;
                this.updateNotificationsEmitter.next(this.notifications);
                clearTimeout(timerId);
            }, 350);
        }
    }

    /**
     * Hide a notification, so it only shows on the notifications page not on the structure
     * @param id
     */
    public showNotificationById(id: string): void {
        this.logService.debug('NotifyService:showNotificationById()', 'Attempting to show notifications ' + id);
        const index = this.notifications.findIndex(item => item.id === id);
        if (index !== -1) {
            this.notifications[index].hidden = false;
            this.addToToasterEmitter.next(this.notifications[index]);
        }
    }

    /**
     * Clear notifications
     */
    public clearAll(): void {
        this.notifications = [];
        this.updateNotificationsEmitter.next(this.notifications);
        this.clearToasterEmitter.next(true);
    }

    /**
     * Clear only local notifications
     */
    public clearLocal(): void {
        this.clearNotificationByType('local');
    }

    /**
     * Format notification title.
     * @param notification
     * @private
     */
    public formatNotificationTitle(notification: NotificationModel): string {
        let message: string = notification.message;
        if (notification.messageTranslate) {
            message = this.languageService.translate(notification.message);
        }

        const formattedString = this.utilsService.removeHTMLFromString(message);

        return formattedString.split(/\s+/).join(' ');
    }

    /**
     * Create error notifications
     * @param {string} message
     * @param {object} options
     *  pageReload,
     *  url,
     *  urlTarget,
     *  hideTypeMessage,
     *  callBack,
     *  waiting,
     *  closeButton,
     *  messageTranslate,
     *  errorId
     */
    public error(message: string, options: any = <any>{}): void {
        this.pushNotificationToArray(this.setUpParameters(this.level.ERROR, message, options));
    }

    /**
     * Create warning notifications
     * @param {string} message
     * @param {object} options
     *  pageReload,
     *  url,
     *  urlTarget,
     *  hideTypeMessage,
     *  callBack,
     *  waiting,
     *  closeButton,
     *  messageTranslate,
     *  errorId
     */
    public warning(message: string, options: any = <any>{}): void {
        this.pushNotificationToArray(this.setUpParameters(this.level.WARNING, message, options));
    }

    /**
     * Create success notifications
     * @param {string} message
     * @param {object} options
     *  pageReload,
     *  url,
     *  urlTarget,
     *  hideTypeMessage,
     *  callBack,
     *  waiting,
     *  closeButton,
     *  messageTranslate,
     *  errorId
     */
    public success(message: string, options: any = <any>{}): void {
        this.pushNotificationToArray(this.setUpParameters(this.level.SUCCESS, message, options));
    }

    /**
     * Create service restart/reload notifications
     * @param {string} message
     * @param {{}} options
     *  pageReload,
     *  url,
     *  urlTarget,
     *  hideTypeMessage,
     *  callBack,
     *  waiting,
     *  closeButton,
     *  messageTranslate,
     *  errorId
     */
    public info(message: string, options: any = <any>{}): void {
        this.pushNotificationToArray(this.setUpParameters(this.level.INFO, message, options));
    }

    /**
     * Create debug notifications
     * @param {string} message
     * @param {{}} options
     *  pageReload,
     *  url,
     *  urlTarget,
     *  hideTypeMessage,
     *  callBack,
     *  waiting,
     *  closeButton,
     *  messageTranslate,
     *  errorId
     */
    public debug(message: string, options: any = <any>{}): void {
        if (!environment.production) {
            this.pushNotificationToArray(this.setUpParameters(this.level.DEBUG, message, options));
        } else {
            this.logService.warn('Notification Service: debug',
                'A debug notifications is being created while the application is in production mode');
        }
    }

    /**
     * Set up the parameters for the notification.
     * @param level
     * @param message
     * @param options
     * @private
     */
    private setUpParameters(level: number, message: string, options: any = <any>{}): NotificationModel {
        return {
            id: (options.id ? options.id : this.utilsService.generateUniqueId(message)),
            title: this.utilsService.removeHTMLFromString(message),
            type: this.getNotificationType(options),
            level,
            message: message,
            hidden: false,
            date: new Date(),
            pageReload: (options.pageReload ? options.pageReload : null),
            url: (options.url ? options.url : null),
            urlTarget: (options.urlTarget ? options.urlTarget : '_self'),
            urlCloseOnClick: (options.urlCloseOnClick ? options.urlCloseOnClick : false),
            hideTypeMessage: (options.hideTypeMessage ? options.hideTypeMessage : null),
            callBack: (options.callBack ? options.callBack : null),
            waiting: (options.waiting ? options.waiting : null),
            closeButton: (options.closeButton ? options.closeButton : level === 1 ? true : null),
            messageTranslate: (options.messageTranslate ? options.messageTranslate : null),
            errorId: (options.errorId ? options.errorId : null)
        };
    }

    /**
     * Get the message type from options or returns 'general' if no match.
     * @param options
     * @private
     */
    private getNotificationType(options: any): NotificationModel['type'] {
        if (options.system !== undefined) {
            return 'system';
        } else if (options.static !== undefined) {
            return 'static';
        } else if (options.header !== undefined) {
            return 'header';
        } else if (options.local !== undefined) {
            return 'local';
        } else if (options.wizardError !== undefined) {
            return 'wizardError';
        } else {
            return 'general';
        }
    }

    /**
     * Clear notification by type
     * @param type
     * @private
     */
    private clearNotificationByType(type: string): void {
        let index: number = 0;
        for (const notification of this.notifications) {
            if (notification.type === type) {
                this.notifications.splice(index, 1);
                this.removeFromToasterEmitter.next(notification);
            }
            index++;
        }
        this.updateNotificationsEmitter.next(this.notifications);
    }

    /**
     * Push notification to the notifications array.
     * @param {NotificationModel} notification
     */
    private pushNotificationToArray(notification: NotificationModel): void {
        if (this.hasPermissions(notification)) {
            const hasDuplicate = this.findDuplicate(notification.id, notification.message);
            // Check if a duplicate static notification exists and exit if it does.
            if (hasDuplicate && notification.type === 'static') {
                return;
            }

            if (!this._muteLocal) {
                if (notification.level === 1 || notification.level === 3) {
                    this.removeNotificationById(notification.id);
                }

                this.notifications.push(notification);
                this.updateNotificationsEmitter.next(this.notifications);
                this.addToToasterEmitter.next(notification);
            }
        }
    }

    /**
     * Check against RBAC if permission exists for notifications that feature a callback/url/action.
     * @param {NotificationModel} notification
     * @private
     */
    private hasPermissions(notification: NotificationModel): boolean {
        // Filter service ids
        const serviceNotificationIds = ['servicePrompt', 'restart-error', 'shutdown-error', 'restartHapProxy'];
        if (serviceNotificationIds.includes(notification.id)) {
            const permissionsReference = {
                'reload': [{ path: '/services/:service/reload', methods: ['PUT'] }],
                'restart': [{ path: '/services/:service/restart', methods: ['PUT'] }],
            };

            return this.checkRbacPermissions(permissionsReference);
        }

        // filter cluster if starts with 'notifyMismatchWarning'
        if (notification.id.startsWith('notifyMismatchWarning')) {
            const permissionsReference = {
                'reboot': [{ path: '/system/reboot', methods: ['PUT'] }],
            };

            return this.checkRbacPermissions(permissionsReference);
        }

        // filter update ids
        const systemUpdateNotificationIds = ['notify_system_update', 'update-error'];
        if (systemUpdateNotificationIds.includes(notification.id)) {
            const permissionsReference = {
                'update': [{ path: '/system/update', methods: ['POST', 'PUT'] }]
            };

            return this.checkRbacPermissions(permissionsReference);
        }

        // filter license ids
        const licenseNotificationIds = ['updateLicenceKey', 'invalidLicenceKey'];
        if (licenseNotificationIds.includes(notification.id)) {
            const permissionsReference = {
                'license': [{ path: '/license', methods: ['PUT'] }],
            };

            return this.checkRbacPermissions(permissionsReference);
        }

        // filter by locks
        const filterLocksNotificationIds = ['lock-notify'];
        if (filterLocksNotificationIds.includes(notification.id)) {
            const permissionsReference = {
                'locks': [{ path: '/locks', methods: ['DELETE'] }],
                'locks-apps': [{ path: '/locks/apps', methods: ['DELETE'] }],
                'locks-user': [{ path: '/locks/user', methods: ['DELETE'] }],
            };

            return this.checkRbacPermissions(permissionsReference);
        }

        // If no ids are matched, return true to show the notification.
        return true;
    }

    /**
     * Check against RBAC for permissions.
     * @param permissionsReference
     * @private
     */
    private checkRbacPermissions(permissionsReference: any,): boolean {
        const permissions = this.rbacService.getPermissionsLookup(permissionsReference);

        return Object.values(permissions)?.every(item => item);
    }

    /**
     * Check for a duplicate notification matching ID and Message.
     * @param id
     * @param message
     * @private
     */
    private findDuplicate(id: any, message: string): boolean {
        return this.notifications.some((item: NotificationModel) => item.id === id && item.message === message);
    }

    /**
     * Show notifications (for use with API service).
     * @param type
     * @param message
     * @param notificationId
     * @param errorId
     */
    public showNotification(type: string, message: string, notificationId?: string, errorId?: string): void {
        switch (type) {
            case 'busy':
            case 'warning': {
                this.warning(message, { id: notificationId, errorId, static: true });
                break;
            }
            case 'error':
            case 'fail':
            case 'forbidden': {
                this.error(message, { id: notificationId, errorId });
                break;
            }
            default: {
                this.success(message, { id: notificationId, errorId });
                break;
            }
        }
    }
}
